import { NumberInput, Select, TextInput } from "@mantine/core";
import type { ReactElement } from "react";
import type { TFunction } from "i18next";

import type { ConfigKeySpec, ConfigValue } from "./api";
import { useConfigKeys } from "./useConfigKeys";

// Форма apply `config_update` для ProposalCard. Реестр-Select ключа,
// поле значения с валидацией по схеме (тип/диапазон) и чистые хелперы.
// Зеркало `role_change` (Select модели), но значение подтверждает человек
// в типизированном поле, а не выбором из списка.

// Строковый тип значения int-ключа реестра (см. ConfigKeySpec.value_type).
// Для int-ключа рисуем NumberInput с границами; для прочих — TextInput.
const INT_VALUE_TYPE = "int";

// Mantine NumberInput может отдать bigint при огромном вводе; наш state и
// валидатор работают с number/string — приводим bigint к number. Вынесено
// чистой функцией: onChange NumberInput в jsdom не зовётся.
export function coerceNumberInput(
  value: number | string | bigint,
): number | string {
  return typeof value === "bigint" ? Number(value) : value;
}

// Результат проверки введённого значения config_update по схеме ключа.
// `value` — типизированное значение, готовое в apply (или null, если
// невалидно/пусто); `error` — i18n-ключ причины (или null, если ок).
export interface ConfigValueCheck {
  readonly value: ConfigValue | null;
  readonly error: string | null;
}

// Чистая валидация введённого значения по схеме выбранного ключа. Для
// int-ключа: значение обязано быть числом и лежать в [min_value,
// max_value] включительно. Пустой ввод (null/"") — не ошибка, просто
// «ещё не заполнено» (value=null, error=null) → кнопка остаётся disabled.
// Зеркалит ConfigKeySpec.validate на backend (тип/диапазон). Вынесена
// чистой функцией — NumberInput onChange в jsdom не зовётся.
export function validateConfigValue(
  spec: ConfigKeySpec | undefined,
  raw: number | string | null,
): ConfigValueCheck {
  if (spec === undefined || raw === null || raw === "") {
    return { value: null, error: null };
  }
  if (spec.value_type === INT_VALUE_TYPE) {
    return validateIntValue(spec, raw);
  }
  // Прочие типы (будущие str/bool-ключи): принимаем строку как есть.
  return { value: String(raw), error: null };
}

function validateIntValue(
  spec: ConfigKeySpec,
  raw: number | string,
): ConfigValueCheck {
  const num = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isInteger(num)) {
    return { value: null, error: "selfImprovement.configValueNotInt" };
  }
  if (spec.min_value !== null && num < spec.min_value) {
    return { value: null, error: "selfImprovement.configValueTooLow" };
  }
  if (spec.max_value !== null && num > spec.max_value) {
    return { value: null, error: "selfImprovement.configValueTooHigh" };
  }
  return { value: num, error: null };
}

// Поле значения config_update. Экспортируется ради юнит-теста: ветка
// «ключ выбран → рисуем поле» недостижима через Mantine Select onChange в
// jsdom, поэтому рендерим компонент напрямую с переданной схемой.
export function ConfigValueField({
  spec,
  rawValue,
  setRawValue,
  busy,
  t,
}: {
  readonly spec: ConfigKeySpec;
  readonly rawValue: number | string | null;
  readonly setRawValue: (value: number | string | null) => void;
  readonly busy: boolean;
  readonly t: TFunction;
}): ReactElement {
  const check = validateConfigValue(spec, rawValue);
  const error = check.error !== null ? t(check.error) : undefined;
  const hint = t("selfImprovement.configValueHint", {
    default: String(spec.default),
    min: spec.min_value !== null ? String(spec.min_value) : "−∞",
    max: spec.max_value !== null ? String(spec.max_value) : "+∞",
  });
  if (spec.value_type === INT_VALUE_TYPE) {
    return (
      <NumberInput
        label={t("selfImprovement.configValueLabel")}
        description={hint}
        placeholder={t("selfImprovement.configValuePlaceholder")}
        value={rawValue ?? ""}
        onChange={(value) => {
          setRawValue(coerceNumberInput(value));
        }}
        // min/max — только если граница задана: exactOptionalPropertyTypes
        // запрещает явный undefined в опциональном prop'е.
        {...(spec.min_value !== null ? { min: spec.min_value } : {})}
        {...(spec.max_value !== null ? { max: spec.max_value } : {})}
        disabled={busy}
        error={error}
      />
    );
  }
  return (
    <TextInput
      label={t("selfImprovement.configValueLabel")}
      description={hint}
      placeholder={t("selfImprovement.configValuePlaceholder")}
      value={rawValue === null ? "" : String(rawValue)}
      onChange={(event) => {
        setRawValue(event.currentTarget.value);
      }}
      disabled={busy}
      error={error}
    />
  );
}

// Форма config_update: Select ключа из реестра + (если ключ выбран) поле
// значения. Экспортируется ради юнит-теста ветки «ключ выбран»: Mantine
// Select onChange в jsdom не зовётся, поэтому configKey передаём пропом.
export function ConfigUpdateForm({
  configKey,
  setConfigKey,
  rawValue,
  setRawValue,
  busy,
  t,
}: {
  readonly configKey: string | null;
  readonly setConfigKey: (value: string | null) => void;
  readonly rawValue: number | string | null;
  readonly setRawValue: (value: number | string | null) => void;
  readonly busy: boolean;
  readonly t: TFunction;
}): ReactElement {
  const configKeys = useConfigKeys();
  const specs = configKeys.data?.config_keys ?? [];
  const selectedSpec = specs.find((spec) => spec.key === configKey);
  return (
    <>
      <Select
        label={t("selfImprovement.configKeyLabel")}
        description={t("selfImprovement.configKeyHint")}
        placeholder={t("selfImprovement.configKeyPlaceholder")}
        data={specs.map((spec) => spec.key)}
        value={configKey}
        onChange={setConfigKey}
        disabled={configKeys.isLoading || busy}
        error={configKeys.isError ? configKeys.error.message : undefined}
      />
      {selectedSpec !== undefined ? (
        <ConfigValueField
          spec={selectedSpec}
          rawValue={rawValue}
          setRawValue={setRawValue}
          busy={busy}
          t={t}
        />
      ) : null}
    </>
  );
}
